import { lazy, Suspense, useEffect } from 'react';
import './App.css';
import styled from 'styled-components';
import tw from 'twin.macro';
import NavBar from './app/components/navBar';
import { SearchNavSlotProvider } from './app/components/navBar/searchNavSlot';
import HostCarPage from './app/containers/HostCarPage';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { HostCapabilityRoute } from './app/components/HostCapabilityRoute';
import { HostAccountRoute } from './app/components/HostAccountRoute';
import AddCarPage from './app/containers/HostCarPage/AddCarPage';
import AddVinPage from './app/containers/HostCarPage/AddCarPage/AddVinPage';
import AddDetailsForNotCar from './app/containers/HostCarPage/AddCarPage/AddDetailsForNotCar';
import AddPricePage from './app/containers/HostCarPage/AddCarPage/AddPricePage';
import AddMonthlyPricePage from './app/containers/HostCarPage/AddCarPage/AddMonthlyPricePage';
import AddPhotosPage from './app/containers/HostCarPage/AddCarPage/AddPhotosPage';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useInitTokensHook } from './app/hooks/useInitTokensHook';
import ProfilePage from './app/containers/UserPage/ProfilePage';
import TripsAsAdditionalDriverPage from './app/containers/UserPage/TripsAsAdditionalDriverPage';
import PaymentMethodsPage from './app/containers/UserPage/PaymentMethodsPage';
import { client } from './app/services/apollo';
import { ApolloProvider } from '@apollo/client';
import { HostAccountGuardProvider } from './app/hooks/useHostAccountGuard';
import { AuthProvider } from 'react-oidc-context';
import { cognitoAuthConfig } from './services/cognitoService/reactOidcConfig';
import AuthBridge from './app/components/auth/AuthBridge';
import { AdminRouteGuard } from './app/components/auth/AdminRouteGuard';
import { HomeRoute } from './app/components/routing/HomeRoute';
import { ProductPathGuard } from './app/components/routing/ProductPathGuard';
import { ErrorBoundary } from '@sentry/react';
import GuestCarsPage from './app/containers/GuestCarPage';
import GuestViewCarPage from './app/containers/GuestCarPage/ViewCarPage';
import { getBrand } from './app/constants/brands';
import { applyBrandTheme } from './app/utils/brandTheme';
import LoadingSpinner from './app/components/LoadingSpinner';
import StayListingsPage from './app/containers/StayHostPage/ListingsPage';
import StaySearchPage from './app/containers/StayGuestPage/StaySearchPage';
import StayDetailPage from './app/containers/StayGuestPage/StayDetailPage';
import WishlistsPage from './app/containers/StayGuestPage/WishlistsPage';
import StayCheckoutPage from './app/containers/StayBookingPage/StayCheckoutPage';
import StayTripPage from './app/containers/StayBookingPage/StayTripPage';
import StayReservationPage from './app/containers/StayBookingPage/StayReservationPage';
import {
  StayReservationsPage,
  StayTripsPage,
} from './app/containers/StayBookingPage/StayBookingLists';
import { isServicesSite, isStaysSite } from './app/constants/siteConfig';
import StayListingWizardPage from './app/containers/StayHostPage/ListingWizardPage';
import StayListingManagePage from './app/containers/StayHostPage/ListingManagePage';
import StayTodayPage from './app/containers/StayHostPage/TodayPage';
import StayHostReviewsPage from './app/containers/StayHostPage/HostReviewsPage';
import ProviderTodayPage from './app/containers/ServiceProviderPage/ProviderTodayPage';
import ServiceLocationsPage from './app/containers/ServiceProviderPage/LocationsPage';
import WorkersPage from './app/containers/ServiceProviderPage/WorkersPage';
import WorkerWizardPage from './app/containers/ServiceProviderPage/WorkerWizardPage';
import WorkerManagePage from './app/containers/ServiceProviderPage/WorkerManagePage';
import WorkerPricingPage from './app/containers/ServiceProviderPage/WorkerPricingPage';
import WorkerAccountsPage from './app/containers/ServiceProviderPage/WorkerAccountsPage';
import AcceptWorkerInvitePage from './app/containers/ServiceProviderPage/AcceptWorkerInvitePage';
import ProviderTaxInfoPage from './app/containers/ServiceProviderPage/ProviderTaxInfoPage';
import ProviderPayoutsPage from './app/containers/ServiceProviderPage/PayoutsPage';
import AdminServiceProvidersPage from './app/containers/AdminServiceProvidersPage';
import ServiceSearchPage from './app/containers/ServiceGuestPage/ServiceSearchPage';
import WorkerDetailPage from './app/containers/ServiceGuestPage/WorkerDetailPage';
import ServiceBookPage from './app/containers/ServiceGuestPage/ServiceBookPage';
import HandoverStartPage from './app/containers/ServiceGuestPage/HandoverStartPage';
import ServiceSitesPage from './app/containers/ServiceGuestPage/ServiceSitesPage';
import ServiceJobsPage from './app/containers/ServiceGuestPage/ServiceJobsPage';
import ServiceJobDetailPage from './app/containers/ServiceGuestPage/ServiceJobDetailPage';
import ProviderJobPage from './app/containers/ServiceProviderPage/ProviderJobPage';
import WorkerJobsPage from './app/containers/ServiceWorkerPage/WorkerJobsPage';
import WorkerJobPage from './app/containers/ServiceWorkerPage/WorkerJobPage';
import AdminServiceJobsPage from './app/containers/AdminServiceJobsPage';
import AdminServiceWorkersPage from './app/containers/AdminServiceWorkersPage';
import AdminServiceTaxPage from './app/containers/AdminServiceTaxPage';
import AdminServicePayoutsPage from './app/containers/AdminServicePayoutsPage';
import AdminMinimumWagePage from './app/containers/AdminMinimumWagePage';
import AddLocationPage from './app/containers/HostCarPage/AddLocationPage';
import BookingsByCar from './app/containers/HostCarPage/BookingsPage/BookingsByCar';
import BookingDetails from './app/containers/HostCarPage/BookingsPage/BookingDetails';
import { SearchResultPage } from './app/containers/GuestCarPage/SearchResultPage';
import CheckoutPage from './app/containers/GuestCarPage/CheckoutPage';
import ProtectionPlanPage from './app/containers/ProtectPlanPage';
import ProductTypeDetailsPage from './app/containers/ProductTypeDetailsPage';
import TripFeePage from './app/containers/TripFeePage';
import CouponsPage from './app/containers/CouponsPage';
import AgreementPage from './app/containers/DealerPage/agreementPage';
import ChatPage from './app/containers/ChatPage';
import ChatDetailPage from './app/containers/ChatPage/ChatDetailPage';
import BroadcastDetailPage from './app/containers/ChatPage/BroadcastDetailPage';
import DealerPage from './app/containers/DealerPage';
import BuyersGuidePage from './app/containers/DealerPage/BuyersGuidePage';
import MyBookingsPage from './app/containers/MyBookingsPage';
import BookedMyCarsPage from './app/containers/BookedMyCarsPage';
import { RentalAgreement } from './app/containers/HostCarPage/BookingsPage/BookingDetails/agreement/rentalAgreement';
import CarListPage from './app/containers/DealerPage/carlistPage';
import Test4018 from './app/containers/Test4018';
import { ToastAlertModal } from './app/components/toastAlert';
import CancelledBookingsPage from './app/containers/CancelledBookingsPage';
import TermsOfServicePolicyPage from './app/containers/TermsOfServicePolicyPage';
import CancellationPolicyPage from './app/containers/CancellationPolicyPage';
import PrivacyPolicyPage from './app/containers/PrivacyPolicyPage';
import AccountDeletionPage from './app/containers/AccountDeletionPage';
import RenterAgreementPage from './app/containers/RenterAgreementPage';
import HostAgreementPage from './app/containers/HostAgreementPage';
import CarSharingAgreementPage from './app/containers/CarSharingAgreementPage';
import PolicyVersionPage from './app/containers/PolicyPage/PolicyVersionPage';
import AdminPoliciesPage from './app/containers/AdminPoliciesPage';
import { PendingPoliciesModal } from './app/components/PendingPoliciesModal';
import InvoicesByRefundStatusPage from './app/containers/InvoicesByRefundStatusPage';
import ReferralCommissionsPage from './app/containers/ReferralCommissionsPage';
import InvoiceDetailsPage from './app/containers/InvoiceDetailsPage';
import OperatingHoursPage from './app/containers/OperatingHoursPage';
import { DealershipNavBar } from './app/components/DealershipNavBar';
import DealershipLanding from './app/containers/DealershipLanding';
import DealershipContact from './app/containers/DealershipContact';
import DealershipFooter from './app/components/DealershipFooter';
import { Footer } from './app/components/footer';
import ViewDealerCarPage from './app/containers/HostCarPage/ViewDealerCarPage';
import HostViewCarPage from './app/containers/HostCarPage/ViewCarPage';
import PDFViewer from './app/containers/DealershipLanding/CarFaxViewer';
import OptInWorkflowPage from './app/containers/OptInWorkflowPage';
import ProfilePageSkeleton from './app/components/skeletons/ProfilePageSkeleton';
import HostCarPageSkeleton from './app/components/skeletons/HostCarPageSkeleton';
import ViewCarPageSkeleton from './app/components/skeletons/ViewCarPageSkeleton';
import CheckoutPageSkeleton from './app/components/skeletons/CheckoutPageSkeleton';
import AddCarPageSkeleton from './app/components/skeletons/AddCarPageSkeleton';
import GuestCarsPageSkeleton from './app/components/skeletons/GuestCarsPageSkeleton';
import BookingsByCarSkeleton from './app/components/skeletons/BookingsByCarSkeleton';
import BookingDetailsSkeleton from './app/components/skeletons/BookingDetailsSkeleton';
import SearchResultsSkeleton from './app/components/skeletons/SearchResultsSkeleton';
import DealerPageSkeleton from './app/components/skeletons/DealerPageSkeleton';
import MyBookingsPageSkeleton from './app/components/skeletons/MyBookingsPageSkeleton';
import BookedMyCarsPageSkeleton from './app/components/skeletons/BookedMyCarsPageSkeleton';
import TripFeePageSkeleton from './app/components/skeletons/TripFeePageSkeleton';
import ProtectionPlanPageSkeleton from './app/components/skeletons/ProtectionPlanPageSkeleton';
import ViewDealerCarSkeleton from './app/components/skeletons/ViewDealerCarSkeleton';
import InvoiceDetailsSkeleton from './app/components/skeletons/InvoiceDetailsSkeleton';
import LocationsPageSkeleton from './app/components/skeletons/LocationsPageSkeleton';
import { useChatNotifications } from './app/hooks/useChatNotifications';
import { useFcmInitialization } from './app/hooks/useFcmInitialization';
import HelpPage from './app/containers/HelpPage';
import HostGuidePage from './app/containers/HostGuidePage';
import AboutUsPage from './app/containers/AboutUsPage';
import PaymentApiStoryBoard from './app/containers/PaymentApiStoryBoard';
import ApiUsageAnalysisPage from './app/containers/ApiUsageAnalysis';
import BookingTestFlowPage from './app/containers/BookingTestFlowPage';
import HostRatingsPage from './app/containers/HostCarPage/RatingsPage';
import HostEarningsPage from './app/containers/HostCarPage/EarningsPage';
import DamageClaimsPage from './app/containers/HostCarPage/DamageClaimsPage';
import DamageClaimDetailPage from './app/containers/HostCarPage/DamageClaimDetailPage';
import GuestDamageClaimsPage from './app/containers/GuestCarPage/DamageClaimsPage';
import AdminDamageClaimsPage from './app/containers/AdminDamageClaimsPage';
import AdminClaimDetailPage from './app/containers/AdminDamageClaimsPage/AdminClaimDetailPage';
import AdminLocationsPage from './app/containers/AdminLocationsPage';
import AdminHotLocationsPage from './app/containers/AdminHotLocationsPage';
import AdminBroadcastsPage from './app/containers/AdminBroadcastsPage';
import AdminUnlistedCarsPage from './app/containers/AdminUnlistedCarsPage';
import AdminCardNameChecksPage from './app/containers/AdminCardNameChecksPage';
import AdminPlatformSitesPage from './app/containers/AdminPlatformSitesPage';
import AdminHostApplicationsPage from './app/containers/AdminHostApplicationsPage';
import AdminBrandSettingsPage from './app/containers/AdminBrandSettingsPage';
import AdminPageSettingsPage from './app/containers/AdminPageSettingsPage';
import AdminBrandsPage from './app/containers/AdminBrandsPage';
import AdminAppFeaturesPage from './app/containers/AdminAppFeaturesPage';
import AdminVerifyGeneralDriversLicensePage from './app/containers/AdminVerifyGeneralDriversLicensePage';
import ShortIdManagerPage from './app/containers/ShortIdManagerPage';
import MockAppTimePage from './app/containers/Dev/MockAppTimePage';
import { MockTimeBadge } from './app/components/MockTimeBadge';
import { TestModeBanner } from './app/components/EnvironmentSwitchSection';
import { appTimeActions, isDevApiEnv } from './store/appTimeStore';

const AppContainer = styled.div`
  ${tw`w-full max-w-full min-w-0 flex flex-col min-h-screen overflow-x-hidden box-border`}
  display: flex;
  flex-direction: column;
  padding: 0;
  margin: 0;
`;

/** Single DOM root inside Router so flex layout does not hit NavBar via nth-child. */
const AppLayoutRoot = styled.div`
  ${tw`flex flex-col items-stretch flex-1 w-full min-w-0 min-h-0 max-w-full overflow-x-hidden box-border`}
  width: 100%;
`;

const MainContent = styled.main`
  ${tw`flex-1 w-full min-w-0 min-h-0 max-w-full overflow-x-hidden box-border`}
`;

// Component to handle chat notifications (must be inside Router)
const ChatNotificationsHandler: React.FC = () => {
  useChatNotifications();
  return null;
};

// Component to handle FCM initialization (must be inside Router)
const FcmInitializationHandler: React.FC = () => {
  useFcmInitialization();
  return null;
};

// Component to conditionally render Footer based on route (must be inside Router)
const FooterWrapper: React.FC = () => {
  const location = useLocation();

  // Define routes where Footer should be hidden
  const hideFooterRoutes = [
    '/cars/guest/view',
    '/cars/guest/search',
    '/checkout',
    '/chat',
    '/host/profile',
    '/admin',
    '/test',
  ];

  const showFooter = !hideFooterRoutes.some(route => location.pathname.includes(route));

  return showFooter ? <Footer /> : null;
};

function App() {
  const isInitialized = useInitTokensHook();

  useEffect(() => {
    applyBrandTheme(getBrand());
    appTimeActions.init();
  }, []);

  // Add a check for the domain
  const isDealershipSite =
    window.location.hostname.includes('autopiloteecars.com') ||
    window.location.hostname.includes('jidosojuholding.com');

  // You could set the dealer ID based on domain, URL params, or other logic
  // For example, if different dealerships have different subdomains:

  const renderDealershipRoutes = () => (
    <>
      <Routes>
        <Route path="/" element={<DealershipLanding />} />
        <Route path="/cars_on_sale" element={<CarListPage />} />
        <Route path="/contact" element={<DealershipContact />} />
        <Route path="/car-view/:carId" element={<ViewDealerCarPage />} />
        <Route path="/carfax/:carId" element={<PDFViewer />} />
        <Route path="/phone-verification-opt-in" element={<OptInWorkflowPage />} />
        {/* Catch-all route for 404 errors - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <DealershipFooter />
    </>
  );

  const renderMainRoutes = () => {
    if (!isInitialized) {
      return (
        <Routes>
          <Route path="/" element={<HomeRoute loading />} />
          <Route path="/profile" element={<ProfilePageSkeleton />} />
          <Route path="/trips-as-additional-driver" element={<ProfilePageSkeleton />} />
          <Route path="/payment-methods" element={<ProfilePageSkeleton />} />
          <Route path="/cars/host" element={<HostCarPageSkeleton />} />
          <Route path="/cars/host/view/:id" element={<ViewCarPageSkeleton />} />
          <Route path="/cars/guest/view/:id" element={<ViewCarPageSkeleton />} />
          <Route path="/cars/guest/pay/:id/:bookingId" element={<CheckoutPageSkeleton />} />
          <Route path="/cars/host/add" element={<AddCarPageSkeleton />} />
          <Route path="/cars/host/add-vin" element={<AddCarPageSkeleton />} />
          <Route path="/cars/host/add-details" element={<AddCarPageSkeleton />} />
          <Route path="/cars/host/add-price" element={<AddCarPageSkeleton />} />
          <Route path="/cars/host/add-monthly-price" element={<AddCarPageSkeleton />} />
          <Route path="/cars/host/add-photos" element={<AddCarPageSkeleton />} />
          <Route path="/cars/guest" element={<GuestCarsPageSkeleton />} />
          <Route path="/cars/host/add-location" element={<LocationsPageSkeleton />} />
          <Route path="/cars/host/protection-plans" element={<ProtectionPlanPageSkeleton />} />
          <Route path="/cars/host/miscellaneous-fees" element={<TripFeePageSkeleton />} />
          <Route path="/cars/host/bookings/:carId" element={<BookingsByCarSkeleton />} />
          <Route path="/cars/guest/booking/:bookingId" element={<BookingDetailsSkeleton />} />
          <Route path="/cars/host/booking/:bookingId" element={<BookingDetailsSkeleton />} />
          <Route path="/cars/guest/search" element={<SearchResultsSkeleton />} />
          <Route path="/dealer/agreement" element={<DealerPageSkeleton />} />
          <Route path="/dealer/buyers-guide" element={<DealerPageSkeleton />} />
          <Route path="/dealership" element={<DealerPageSkeleton />} />
          <Route path="/guest/mybookings" element={<MyBookingsPageSkeleton />} />
          <Route path="/bookedmycars" element={<BookedMyCarsPageSkeleton />} />
          <Route path="/dealership/carlist/:dealerId" element={<DealerPageSkeleton />} />
          <Route path="/invoice/:id" element={<InvoiceDetailsSkeleton />} />
          <Route path="/operating-hours/:locationId" element={<LocationsPageSkeleton />} />
          <Route path="/cancelled-bookings" element={<MyBookingsPageSkeleton />} />
          <Route path="/policies/:doc/versions/:id" element={<PolicyVersionPage />} />
          <Route path="/policies/car-sharing-agreement" element={<CarSharingAgreementPage />} />
          <Route path="/policies/terms" element={<TermsOfServicePolicyPage />} />
          <Route path="/policies/cancellation" element={<CancellationPolicyPage />} />
          <Route path="/policies/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/policies/renter-agreement" element={<RenterAgreementPage />} />
          <Route path="/policies/host-agreement" element={<HostAgreementPage />} />
          <Route path="/account-deletion" element={<AccountDeletionPage />} />
          <Route path="/phone-verification-opt-in" element={<OptInWorkflowPage />} />
          <Route path="/signIn" element={<HomeRoute loading />} />
          <Route path="/signUpVerify" element={<HomeRoute loading />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/host-guide/zh" element={<HostGuidePage language="zh" />} />
          <Route path="/host-guide/en" element={<HostGuidePage language="en" />} />
          <Route path="/payment-api-story-board" element={<PaymentApiStoryBoard />} />
          <Route path="*" element={<LoadingSpinner />} />
        </Routes>
      );
    }
    return (
      <Routes>
        <Route path="/" element={<HomeRoute />} />
        {/* Stays (spec 016). ProductPathGuard keeps them off car sites. */}
        <Route path="/stays/search" element={<StaySearchPage />} />
        <Route path="/stays/book/:id" element={<StayCheckoutPage />} />
        <Route path="/stays/book/:id/:bookingId" element={<StayCheckoutPage />} />
        <Route path="/stays/guest/booking/:bookingId" element={<StayTripPage />} />
        <Route path="/stays/host" element={<StayTodayPage />} />
        <Route path="/stays/host/reviews" element={<StayHostReviewsPage />} />
        <Route path="/stays/host/claims" element={<DamageClaimsPage />} />
        <Route path="/stays/host/claims/:claimId" element={<DamageClaimDetailPage />} />
        <Route path="/stays/host/listings" element={<StayListingsPage />} />
        <Route path="/stays/host/listings/new" element={<StayListingWizardPage />} />
        <Route path="/stays/host/listings/:id/edit" element={<StayListingWizardPage />} />
        <Route path="/stays/host/listings/:id" element={<StayListingManagePage />} />
        <Route path="/stays/host/reservations" element={<StayReservationsPage />} />
        <Route path="/stays/host/booking/:bookingId" element={<StayReservationPage />} />
        <Route path="/stays/:id" element={<StayDetailPage />} />
        {/* Services (spec 019). ProductPathGuard keeps them off the car and stays sites. */}
        <Route path="/services/provider" element={<ProviderTodayPage />} />
        <Route path="/services/provider/locations" element={<ServiceLocationsPage />} />
        <Route path="/services/provider/workers" element={<WorkersPage />} />
        <Route path="/services/provider/workers/new" element={<WorkerWizardPage />} />
        <Route path="/services/provider/workers/:id/edit" element={<WorkerWizardPage />} />
        <Route path="/services/provider/workers/:id/pricing" element={<WorkerPricingPage />} />
        <Route path="/services/provider/workers/:id/accounts" element={<WorkerAccountsPage />} />
        <Route path="/services/provider/workers/:id" element={<WorkerManagePage />} />
        <Route path="/services/provider/payouts" element={<ProviderPayoutsPage />} />
        <Route path="/services/provider/tax-info" element={<ProviderTaxInfoPage />} />
        <Route path="/accept-worker-invite" element={<AcceptWorkerInvitePage />} />
        <Route path="/services/search" element={<ServiceSearchPage />} />
        <Route path="/services/workers/:id" element={<WorkerDetailPage />} />
        <Route path="/services/book" element={<HandoverStartPage />} />
        <Route path="/services/book/:workerId" element={<ServiceBookPage />} />
        <Route path="/services/book/:workerId/:bookingId" element={<ServiceBookPage />} />
        <Route path="/services/sites" element={<ServiceSitesPage />} />
        <Route path="/services/jobs" element={<ServiceJobsPage />} />
        <Route path="/services/jobs/:bookingId" element={<ServiceJobDetailPage />} />
        <Route path="/services/provider/jobs/:bookingId" element={<ProviderJobPage />} />
        <Route path="/services/worker" element={<WorkerJobsPage />} />
        <Route path="/services/worker/jobs/:bookingId" element={<WorkerJobPage />} />
        <Route path="/wishlists" element={<WishlistsPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/trips-as-additional-driver" element={<TripsAsAdditionalDriverPage />} />
        <Route path="/payment-methods" element={<PaymentMethodsPage />} />
        <Route path="/cars/host" element={<HostCarPage />} />
        <Route
          path="/cars/host/view/:id"
          element={
            <HostAccountRoute>
              <HostViewCarPage />
            </HostAccountRoute>
          }
        />
        <Route path="/cars/guest/view/:id" element={<GuestViewCarPage />} />
        <Route path="/cars/guest/pay/:id/:bookingId" element={<CheckoutPage />} />
        <Route
          path="/cars/host/add"
          element={
            <HostAccountRoute>
              <AddCarPage />
            </HostAccountRoute>
          }
        />
        <Route
          path="/cars/host/add-vin"
          element={
            <HostAccountRoute>
              <AddVinPage />
            </HostAccountRoute>
          }
        />
        <Route
          path="/cars/host/add-details"
          element={
            <HostAccountRoute>
              <AddDetailsForNotCar />
            </HostAccountRoute>
          }
        />
        <Route
          path="/cars/host/add-price"
          element={
            <HostAccountRoute>
              <AddPricePage />
            </HostAccountRoute>
          }
        />
        <Route
          path="/cars/host/add-monthly-price"
          element={
            <HostAccountRoute>
              <AddMonthlyPricePage />
            </HostAccountRoute>
          }
        />
        <Route
          path="/cars/host/add-photos"
          element={
            <HostAccountRoute>
              <AddPhotosPage />
            </HostAccountRoute>
          }
        />
        <Route path="/cars/guest" element={<GuestCarsPage />} />
        <Route
          path="/cars/host/add-location"
          element={
            <HostAccountRoute>
              <AddLocationPage />
            </HostAccountRoute>
          }
        />
        <Route
          path="/cars/host/protection-plans"
          element={
            <HostAccountRoute>
              <HostCapabilityRoute capability="insurance">
                <ProtectionPlanPage />
              </HostCapabilityRoute>
            </HostAccountRoute>
          }
        />
        <Route
          path="/cars/host/product-type-details"
          element={
            <HostCapabilityRoute capability="couponsAndDealerTools">
              <ProductTypeDetailsPage />
            </HostCapabilityRoute>
          }
        />
        <Route
          path="/cars/host/miscellaneous-fees"
          element={
            <HostAccountRoute>
              <HostCapabilityRoute capability="tripFees">
                <TripFeePage />
              </HostCapabilityRoute>
            </HostAccountRoute>
          }
        />
        <Route
          path="/cars/host/coupons"
          element={
            <HostAccountRoute>
              <HostCapabilityRoute capability="couponsAndDealerTools">
                <CouponsPage />
              </HostCapabilityRoute>
            </HostAccountRoute>
          }
        />
        <Route path="/cars/host/ratings" element={<HostRatingsPage />} />
        <Route path="/cars/host/earnings" element={<HostEarningsPage />} />
        <Route
          path="/cars/host/damage-claims"
          element={
            <HostAccountRoute>
              <DamageClaimsPage />
            </HostAccountRoute>
          }
        />
        <Route path="/cars/host/damage-claims/:claimId" element={<DamageClaimDetailPage />} />
        <Route path="/cars/host/bookings/:carId" element={<BookingsByCar />} />
        <Route path="/cars/guest/booking/:bookingId" element={<BookingDetails />} />
        <Route path="/cars/host/booking/:bookingId" element={<BookingDetails />} />
        <Route path="/guest/damage-claims" element={<GuestDamageClaimsPage />} />
        <Route path="/guest/damage-claims/:claimId" element={<DamageClaimDetailPage />} />
        <Route path="/cars/guest/search" element={<SearchResultPage />} />
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/chat/:chatId" element={<ChatDetailPage />} />
        <Route path="/broadcast/:broadcastId" element={<BroadcastDetailPage />} />
        <Route path="/dealer/agreement" element={<AgreementPage />} />
        <Route path="/dealer/buyers-guide" element={<BuyersGuidePage />} />
        <Route
          path="/dealership"
          element={
            <HostCapabilityRoute capability="couponsAndDealerTools">
              <DealerPage />
            </HostCapabilityRoute>
          }
        />
        <Route
          path="/guest/mybookings"
          element={
            isStaysSite() ? (
              <StayTripsPage />
            ) : isServicesSite() ? (
              <ServiceJobsPage />
            ) : (
              <MyBookingsPage />
            )
          }
        />
        <Route path="/bookedmycars" element={<BookedMyCarsPage />} />
        <Route
          path="/dealership/carlist/:dealerId"
          element={
            <HostCapabilityRoute capability="couponsAndDealerTools">
              <CarListPage />
            </HostCapabilityRoute>
          }
        />
        <Route path="/agreement/:bookingId" element={<RentalAgreement />} />
        <Route path="/test/4018" element={<Test4018 />} />
        <Route path="/cancelled-bookings" element={<CancelledBookingsPage />} />
        <Route
          path="/invoices/refund-status"
          element={
            <HostAccountRoute>
              <HostCapabilityRoute capability="refundReview">
                <InvoicesByRefundStatusPage />
              </HostCapabilityRoute>
            </HostAccountRoute>
          }
        />
        <Route path="/invoice/:id" element={<InvoiceDetailsPage />} />
        <Route path="/operating-hours/:locationId" element={<OperatingHoursPage />} />
        <Route path="/policies/:doc/versions/:id" element={<PolicyVersionPage />} />
        <Route path="/policies/car-sharing-agreement" element={<CarSharingAgreementPage />} />
        <Route path="/policies/terms" element={<TermsOfServicePolicyPage />} />
        <Route path="/policies/cancellation" element={<CancellationPolicyPage />} />
        <Route path="/policies/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/policies/renter-agreement" element={<RenterAgreementPage />} />
        <Route path="/policies/host-agreement" element={<HostAgreementPage />} />
        <Route path="/account-deletion" element={<AccountDeletionPage />} />
        <Route path="/phone-verification-opt-in" element={<OptInWorkflowPage />} />
        <Route path="/signIn" element={<HomeRoute />} />
        <Route path="/signUpVerify" element={<HomeRoute />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/host-guide/zh" element={<HostGuidePage language="zh" />} />
        <Route path="/host-guide/en" element={<HostGuidePage language="en" />} />
        <Route path="/about-us" element={<AboutUsPage />} />
        <Route path="/payment-api-story-board" element={<PaymentApiStoryBoard />} />
        <Route path="/api-usage-analysis" element={<ApiUsageAnalysisPage />} />
        <Route
          path="/manage-short-id"
          element={
            <HostCapabilityRoute capability="couponsAndDealerTools">
              <ShortIdManagerPage />
            </HostCapabilityRoute>
          }
        />
        <Route path="/booking-test-flow" element={<BookingTestFlowPage />} />
        {isDevApiEnv() && <Route path="/dev/datetime" element={<MockAppTimePage />} />}
        {/* Admin Routes */}
        <Route path="/admin" element={<AdminRouteGuard />}>
          <Route path="damage-claims" element={<AdminDamageClaimsPage />} />
          <Route path="damage-claims/:claimId" element={<AdminClaimDetailPage />} />
          <Route path="delivery-locations" element={<AdminLocationsPage />} />
          <Route path="hot-locations" element={<AdminHotLocationsPage />} />
          <Route path="broadcasts" element={<AdminBroadcastsPage />} />
          <Route path="unlisted-cars" element={<AdminUnlistedCarsPage />} />
          <Route path="card-name-checks" element={<AdminCardNameChecksPage />} />
          <Route path="platform-sites" element={<AdminPlatformSitesPage />} />
          <Route path="user-status" element={<AdminHostApplicationsPage />} />
          <Route path="brand-settings" element={<AdminBrandSettingsPage />} />
          <Route path="page-settings" element={<AdminPageSettingsPage />} />
          <Route path="policies" element={<AdminPoliciesPage />} />
          <Route path="referral-commissions" element={<ReferralCommissionsPage />} />
          <Route path="brands" element={<AdminBrandsPage />} />
          <Route path="app-features" element={<AdminAppFeaturesPage />} />
          <Route path="service-providers" element={<AdminServiceProvidersPage />} />
          <Route path="service-jobs" element={<AdminServiceJobsPage />} />
          <Route path="service-workers" element={<AdminServiceWorkersPage />} />
          <Route path="service-tax" element={<AdminServiceTaxPage />} />
          <Route path="service-payouts" element={<AdminServicePayoutsPage />} />
          <Route path="minimum-wages" element={<AdminMinimumWagePage />} />
          <Route
            path="verify-general-drivers-license"
            element={<AdminVerifyGeneralDriversLicensePage />}
          />
        </Route>
        {/* Catch-all route for 404 errors - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  };

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  if (!googleClientId) {
  }

  const renderContentByUrl = () => {
    if (isDealershipSite) {
      return (
        <>
          <DealershipNavBar />
          <MainContent>{renderDealershipRoutes()}</MainContent>
        </>
      );
    }
    return (
      <>
        <TestModeBanner />
        <NavBar />
        <MainContent>
          <ProductPathGuard>{renderMainRoutes()}</ProductPathGuard>
        </MainContent>
        <FooterWrapper />
      </>
    );
  };

  return (
    <ErrorBoundary
      fallback={({ error }: { error: any }) => <div>Something went wrong: {error.message}</div>}
      showDialog
    >
      <AuthProvider {...cognitoAuthConfig}>
        <AuthBridge />
        <ApolloProvider client={client}>
          <AppContainer>
            <HostAccountGuardProvider>
              <Router>
                <AppLayoutRoot>
                  {/* Only mount ChatNotificationsHandler after app is fully initialized */}
                  {isInitialized && <ChatNotificationsHandler />}
                  <FcmInitializationHandler />
                  {isDevApiEnv() && <MockTimeBadge />}
                  <SearchNavSlotProvider>{renderContentByUrl()}</SearchNavSlotProvider>
                  <PendingPoliciesModal />
                </AppLayoutRoot>
              </Router>
            </HostAccountGuardProvider>
            <ToastContainer position="top-center" autoClose={3000} />
            <ToastAlertModal />
          </AppContainer>
        </ApolloProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
